import { FC } from 'react';
import { format } from 'date-fns';
import { IVisitTemplate, IVisitSelection } from '../../types';

interface IReviewConfirmStepProps {
  visitTemplates: IVisitTemplate[];
  selectedVisits: number[];
  visitSelections: Map<number, IVisitSelection>;
  patientName: string;
  trialName: string;
  locationName: string;
  isSubmitting: boolean;
  isSingleVisit?: boolean;
}

export const ReviewConfirmStep: FC<IReviewConfirmStepProps> = ({
  visitTemplates,
  selectedVisits,
  visitSelections,
  patientName,
  trialName,
  locationName,
  isSubmitting,
  isSingleVisit,
}) => (
  <>
    <div className="multi-appointment-info-box">
      {isSingleVisit
        ? <>Review the appointment below. It will be created with <strong>Pending</strong> status. The patient will receive a notification.</>
        : <>Review the appointments below. All {selectedVisits.length} appointments will be created with <strong>Pending</strong> status. The patient will receive a notification.</>
      }
    </div>

    <section className="form-section">
      <h2 className="form-title title-md is-underlined">Appointment Summary</h2>
    </section>

    <section className="form-section">
      <div className="multi-appointment-review-header">
        <div className="multi-appointment-review-meta">
          <div>
            <span className="multi-appointment-review-label">Patient</span>
            <div className="multi-appointment-review-value">{patientName}</div>
          </div>
          <div>
            <span className="multi-appointment-review-label">Trial</span>
            <div className="multi-appointment-review-value">{trialName}</div>
          </div>
          <div>
            <span className="multi-appointment-review-label">Location</span>
            <div className="multi-appointment-review-value">{locationName}</div>
          </div>
        </div>
      </div>

      <table className="multi-appointment-summary-table">
        <thead>
          <tr>
            <th>Visit</th>
            <th>Type</th>
            <th>Date</th>
            <th>Time</th>
            <th>Duration</th>
            <th>MD</th>
          </tr>
        </thead>
        <tbody>
          {selectedVisits.map((visitIndex, orderIndex) => {
            const visit = visitTemplates[visitIndex];

            const selection = visitSelections.get(visitIndex);

            if (!visit || !selection?.selectedSlot) return null;

            return (
              <tr key={visitIndex}>
                <td>
                  <div className="multi-appointment-visit-num">{orderIndex + 1}</div>
                </td>
                <td>
                  <strong>{visit.type || visit.name}</strong>
                </td>
                <td>
                  {(() => {
                    const [year, month, day] = selection.selectedDate.split('-');

                    const localDate = new Date(Number(year), Number(month) - 1, Number(day));

                    return format(localDate, 'MMM d, yyyy');
                  })()}
                </td>
                <td>
                  {selection.selectedSlot.display_from} — {selection.selectedSlot.display_to}
                </td>
                <td>{visit.duration} min</td>
                <td>{visit.md ? 'Yes' : 'No'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>

    {!isSingleVisit && (
      <div className="multi-appointment-warning-box">
        <strong>Note:</strong> All {selectedVisits.length} appointments will be created atomically. If
        any visit fails validation (capacity limits, blackouts), none will be created.
      </div>
    )}

    {isSubmitting && (
      <div className="multi-appointment-loading-text">
        Creating appointments...
      </div>
    )}
  </>
);
